/**
 * The formatting the debug panel and its Markdown report share.
 *
 * One module so a number reads the same in the panel and in the report a
 * developer pastes into an agent — two spellings of the same token count would
 * read as two different measurements.
 *
 * Every formatter resolves the locale per call rather than at import: the panel
 * is translated, and a formatter built once would keep whichever language
 * happened to be active when this module first loaded. The instances are kept
 * per locale rather than rebuilt per call, because the composer's ring runs
 * through here on every streamed token — constructing three `Intl` objects that
 * many times is work whose answer never changes.
 */

import type { ContextWindowUsage } from "@shaarp/common";
import i18n from "i18next";

const locale = (): string => i18n.language || "en";

const numberFormats = new Map<string, Intl.NumberFormat>();

const numberFormat = (options?: Intl.NumberFormatOptions): Intl.NumberFormat => {
  const key = `${locale()}|${JSON.stringify(options ?? {})}`;
  const existing = numberFormats.get(key);
  if (existing) return existing;

  const format = new Intl.NumberFormat(locale(), options);
  numberFormats.set(key, format);
  return format;
};

export const fullNumber = (value: number): string =>
  numberFormat().format(value);

export const compactNumber = (value: number): string =>
  numberFormat({ notation: "compact" }).format(value);

export const formatPercent = (value: number, total: number): string =>
  total > 0
    ? numberFormat({
        style: "percent",
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(value / total)
    : "—";

export const formatDate = (value: Date | string): string =>
  new Date(value).toLocaleString(locale());

/**
 * What the composer's ring reads: the token count first, then its share.
 *
 * The prompt **plus the answer the turn wrote**. `estimatedTokensUsed` is the
 * prompt that was sent, so a turn's own answer is missing from it and only
 * arrives in the next one — a long answer moved the ring not at all, which is
 * the opposite of what someone watching it wants to know.
 *
 * The share alone used to be the whole label, and on a 900 000-token window it
 * does not move either: a turn adds 100 to 500 tokens — 0.01% to 0.06% — so
 * eight consecutive turns of a real conversation rendered "0.8%", "0.8%",
 * "0.8%"… The count is what changes, so it leads; the share stays beside it,
 * because that is what says how much room is left.
 *
 * The count is exact rather than compact: two turns 50 tokens apart both round
 * to "7.6k", which is the same standstill one notation further out.
 */
/**
 * What the ring stands for: the prompt that was sent plus the answer the turn
 * wrote, which is what the next prompt will carry.
 *
 * Shared with the gauge rather than recomputed there, because the arc and the
 * figure beside it are one control: filled from the prompt alone while the
 * label added the answer, a long turn drew an arc a third emptier than its own
 * number, and the arc is what the eye reads first.
 */
export const contextRingTokens = (usage: ContextWindowUsage): number =>
  usage.estimatedTokensUsed + (usage.answerTokens ?? 0);

export const formatContextRing = (usage: ContextWindowUsage): string => {
  const used = contextRingTokens(usage);
  const count = fullNumber(used);
  if (usage.contextWindowSize <= 0) return count;

  return `${count} · ${formatPercent(used, usage.contextWindowSize)}`;
};

const bytes = (value: number, compact: boolean): string =>
  numberFormat({
    style: "unit",
    unit: "byte",
    unitDisplay: "narrow",
    ...(compact ? { notation: "compact" as const } : {}),
  }).format(value);

/** A byte count carrying its own unit, rather than an `o` only French reads. */
export const formatBytes = (value: number): string => bytes(value, false);

/** The same, short enough for a table cell. */
export const formatBytesCompact = (value: number): string =>
  bytes(value, true);

/** `début … fin`, or just the opening when the message was too short to split. */
export const formatBounds = (
  start: string | null,
  end: string | null
): string | null => {
  if (!start) return null;
  return end ? `${start} … ${end}` : start;
};

/**
 * The three line endings CommonMark recognises, the lone `\r` included.
 *
 * Text extracted from a PDF or a legacy page, and upstream error messages that
 * quote it, still carry bare carriage returns. Matching only `\r?\n` leaves
 * them in place while the renderer breaks the line there anyway — whatever
 * follows is then read as the document's own structure.
 *
 * Defined once and shared with the report: two copies drifted apart the first
 * time, which is how the `\r` survived in one of them.
 */
export const LINE_ENDING = /\r\n|\r|\n/;

/**
 * Keep free text inside a Markdown table cell.
 *
 * A `|` closes the cell early and shifts every column after it. So does a line
 * ending, which ends the row outright — and `incident.detail` is a raw error
 * message, stack traces included.
 */
export const escapeCell = (text: string): string =>
  text.replace(/\|/g, "\\|").replace(new RegExp(LINE_ENDING, "g"), " ");

/** A signed count, so a gap reads as a direction and not just a size. */
export const formatDelta = (value: number): string =>
  `${value >= 0 ? "+" : ""}${fullNumber(value)}`;
